import Cookies from 'universal-cookie';
import jwt_decode from 'jwt-decode';
import crypto from 'crypto';
import { useEffect } from 'react';

const algorithm = process.env.ALGORITHM as string;
const secretKey = process.env.SECRETKEY as string;
const iv = process.env.IV as string;

export const isNumber = value => {
	return value.length >= 6 ? /^\d+$/.test(value) : false;
};

export const useDebouncedEffect = (effect, deps, delay) => {
	useEffect(() => {
		const handler = setTimeout(() => effect(), delay);

		return () => clearTimeout(handler);
	}, [...(deps || []), delay]);
};

export const translateUrlToPath = (val?: string | null): string => {
	try {
		if (!val) {
			return '';
		}
		return new URL(val).pathname.replace('/', '');
	} catch {
		return '';
	}
};

export const validateEmail = email => {
	return String(email)
		.toLowerCase()
		.match(
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
		);
};

export const getDigit = account => {
	return account && account.slice(account.length - 1);
};

export const getAccount = account => {
	return account && account.slice(0, -1);
};

export const validateCelphone = (phone: number | string) => {
	if (!phone) return false;

	return String(phone).match(/^[0-9]{10,11}$/);
};

export const statusCode = {
	success: 'success',
	error: 'error',
	info: 'info',
};

export const registerCookie = (token: string, expires_in: Date) => {
	const cookies = new Cookies();

	cookies.set('session', token, {
		expires: expires_in,
		path: '/',
		sameSite: 'strict',
	});

	const decoded = jwt_decode(token);

	cookies.set('user', JSON.stringify(decoded), {
		expires: expires_in,
		path: '/',
		sameSite: 'strict',
	});
};

export const registerUser = (data: any) => {
	const cookies = new Cookies();
	cookies.set('user', data);
};

export const getUser = () => {
	const cookies = new Cookies();
	return cookies.get('user');
};

export const getSession = () => {
	const cookies = new Cookies();
	return cookies.get('session');
};

export const networkOptions = {
	context: {
		headers: {
			authorization: `Bearer ${getSession()}`,
		},
	},
};

export const getAuthenticatedHeader = {
	context: {
		headers: {
			authorization: `Bearer ${getSession()}`,
		},
	},
};

export const handleResponseError = e => {
	if (!e.response || !e.response.data) {
		return {
			status: statusCode.error,
			success: false,
			type: 'info',
			msg: 'Oops aconteceu um erro inesperado',
			data: {},
		};
	}
	const { data } = e.response;
	if (data.errors) {
		return {
			status: statusCode.error,
			success: false,
			type: 'info',
			msg: `Encontramos problemas nos seguintes campos:\n${data.errors.join(
				'\n',
			)}`,
		};
	} else {
		if (data.error) {
			if (typeof data.error === 'object') {
				data.error = `${data.error[0].msg}: ${data.error[0].param}`;
			}
		}
		return {
			status: statusCode.error,
			success: false,
			type: 'info',
			msg: data.message || data.error || data,
		};
	}
};

export const encryptString = (text: string): string => {
	const cipher = crypto.createCipheriv(
		algorithm,
		secretKey,
		Buffer.from(iv, 'utf-8'),
	);
	const encrypted = Buffer.concat([cipher.update(text), cipher.final()]);

	return encrypted.toString('hex');
};

export const decryptString = (textHashed: string): string => {
	const decipher = crypto.createDecipheriv(
		algorithm,
		secretKey,
		Buffer.from(iv, 'utf-8'),
	);

	const decrypted = Buffer.concat([
		decipher.update(Buffer.from(textHashed, 'hex')),
		decipher.final(),
	]);

	return decrypted.toString();
};

export const getGtmId = () => process.env.GTM_ID;

export const fromBase64ToHex = (text: string) => {
	return Buffer.from(text, 'base64').toString('hex');
};
